import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { config } from "./config";

const AuthContext = createContext(null);

const TOKEN_KEY = "metabetta_tokens";

function getStoredTokens() {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    if (!raw) return null;
    const tokens = JSON.parse(raw);
    // Check if access token is expired
    if (tokens.expires_at && Date.now() > tokens.expires_at) {
      return { ...tokens, expired: true };
    }
    return tokens;
  } catch {
    return null;
  }
}

function storeTokens(tokens) {
  localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
}

function clearTokens() {
  localStorage.removeItem(TOKEN_KEY);
}

// Build the Cognito hosted UI URL for Google sign-in
function getLoginUrl() {
  const { domain, clientId, redirectUri } = config.cognito;
  const params = new URLSearchParams({
    response_type: "code",
    client_id: clientId,
    redirect_uri: redirectUri,
    identity_provider: "Google",
    scope: "openid email profile",
  });
  return `https://${domain}/oauth2/authorize?${params.toString()}`;
}

// Exchange authorization code for tokens
async function exchangeCode(code) {
  const { domain, clientId, redirectUri } = config.cognito;
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: clientId,
    redirect_uri: redirectUri,
    code,
  });

  const res = await fetch(`https://${domain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });

  if (!res.ok) {
    const err = await res.text();
    throw new Error(`Token exchange failed: ${err}`);
  }

  return res.json();
}

// Refresh access token
async function refreshTokens(refreshToken) {
  const { domain, clientId } = config.cognito;
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    client_id: clientId,
    refresh_token: refreshToken,
  });

  const res = await fetch(`https://${domain}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });

  if (!res.ok) {
    throw new Error("Token refresh failed");
  }

  return res.json();
}

// Decode JWT payload (no verification needed client-side, Cognito verifies)
function decodeJwtPayload(token) {
  try {
    const payload = token.split(".")[1];
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const setUserFromTokens = useCallback((tokens) => {
    if (!tokens || !tokens.id_token) {
      setUser(null);
      return;
    }
    const payload = decodeJwtPayload(tokens.id_token);
    if (payload) {
      setUser({
        email: payload.email,
        name: payload.name || payload.email,
      });
    }
  }, []);

  useEffect(() => {
    const tokens = getStoredTokens();
    if (tokens && !tokens.expired) {
      setUserFromTokens(tokens);
    } else if (tokens && tokens.expired && tokens.refresh_token) {
      // Try to refresh
      refreshTokens(tokens.refresh_token)
        .then((newTokens) => {
          const merged = { ...tokens, ...newTokens, expires_at: Date.now() + newTokens.expires_in * 1000 };
          storeTokens(merged);
          setUserFromTokens(merged);
        })
        .catch(() => {
          clearTokens();
          setUser(null);
        })
        .finally(() => setLoading(false));
      return;
    }
    setLoading(false);
  }, [setUserFromTokens]);

  const handleCallback = async (code) => {
    const tokens = await exchangeCode(code);
    const stored = {
      ...tokens,
      expires_at: Date.now() + tokens.expires_in * 1000,
    };
    storeTokens(stored);
    setUserFromTokens(stored);
    return stored;
  };

  const getToken = async () => {
    let tokens = getStoredTokens();
    if (!tokens) throw new Error("Not authenticated");

    if (tokens.expired && tokens.refresh_token) {
      const newTokens = await refreshTokens(tokens.refresh_token);
      tokens = { ...tokens, ...newTokens, expires_at: Date.now() + newTokens.expires_in * 1000 };
      storeTokens(tokens);
    }

    return tokens.id_token;
  };

  const login = () => {
    window.location.href = getLoginUrl();
  };

  const logout = () => {
    clearTokens();
    setUser(null);
    const { domain, clientId, logoutUri } = config.cognito;
    window.location.href = `https://${domain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, getToken, handleCallback }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
